import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"
import Hls from "hls.js"

export default class extends Controller {
  static targets = ["banner", "offline", "player", "offlineMessage", "video", "connecting"]

  connect() {
    this.isLive = !this.hasBannerTarget || !this.bannerTarget.classList.contains("hidden")
    this.heartbeatTimer = null
    this.hls = null

    this.channel = createConsumer().subscriptions.create("StreamStatusChannel", {
      received: (data) => {
        this.updateStatus(data.is_live)
      }
    })

    if (this.isLive) {
      this.startHeartbeat()
      this.initPlayer()
    }
  }

  disconnect() {
    this.stopHeartbeat()
    this.destroyPlayer()
    if (this.channel) {
      this.channel.unsubscribe()
    }
  }

  updateStatus(isLive) {
    this.isLive = isLive

    if (this.hasBannerTarget) {
      this.bannerTarget.classList.toggle("hidden", !isLive)
    }
    if (this.hasOfflineTarget) {
      this.offlineTarget.classList.toggle("hidden", isLive)
    }
    if (this.hasPlayerTarget) {
      this.playerTarget.classList.toggle("hidden", !isLive)
    }
    if (this.hasOfflineMessageTarget) {
      this.offlineMessageTarget.classList.toggle("hidden", isLive)
    }

    if (isLive) {
      this.startHeartbeat()
      // Only (re)initialize when we don't already have a running player. The
      // 30s heartbeat re-broadcasts is_live=true; without this guard we'd tear
      // down and recreate hls.js every 30s, which reset playback to the live
      // edge (stall) and dropped ABR back to the lowest rendition.
      if (!this.playerStarted) this.initPlayer()
    } else {
      this.stopHeartbeat()
      this.destroyPlayer()
    }
  }

  // Player lifecycle. hls.js is bundled with the app (not loaded from a CDN),
  // so playback can't be broken by an ad-blocker or network that filters
  // third-party script hosts. Safari/iOS still use their native HLS support.
  initPlayer() {
    if (!this.hasVideoTarget) return
    const video = this.videoTarget
    const src = video.dataset.streamUrl
    if (!src) return

    // Show the "starting…" overlay until playback actually begins (covers the
    // transcoder startup gap + retries); hide it on the first 'playing' event.
    this.showConnecting()
    if (!this.connectingBound) {
      this.connectingBound = true
      video.addEventListener("playing", () => this.hideConnecting())
    }

    if (Hls.isSupported()) {
      // Guard against double-init (connect + turbo:load + live toggle).
      this.destroyPlayer()
      // Keep a few segments of cushion off the live edge for stall resilience.
      this.hls = new Hls({ liveSyncDurationCount: 4 })
      this.hls.loadSource(src)
      this.hls.attachMedia(video)
      this.hls.on(Hls.Events.MANIFEST_PARSED, () => {
        this.clearReload()
        this.attemptPlay(video)
      })
      // Recover from transient live errors instead of breaking playback.
      this.hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) return
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          this.hls.recoverMediaError()
        } else {
          // NETWORK_ERROR — including the manifest 404 right after the stream
          // goes live, before the transcoder has produced the playlist (~15-20s),
          // and transient mid-stream blips. Keep retrying until it's available.
          this.scheduleReload(src)
        }
      })
      this.playerStarted = true
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS (Safari/iOS).
      if (!this.nativeBound) {
        this.nativeBound = true
        video.addEventListener("loadedmetadata", () => { this.clearReload(); this.attemptPlay(video) })
        video.addEventListener("error", () => this.scheduleReload(src))
      }
      if (video.src !== src) { video.src = src }
      this.playerStarted = true
    }
  }

  destroyPlayer() {
    this.clearReload()
    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
    this.playerStarted = false
  }

  // Retry loading the stream until the playlist is available. Covers the
  // startup gap (the transcoder needs ~15-20s to publish the master playlist
  // after the "live" signal fires) and transient mid-stream network errors.
  // Stops automatically when the stream goes offline (destroyPlayer clears it).
  scheduleReload(src) {
    if (this.reloadTimer || !this.playerStarted) return
    this.reloadTimer = setTimeout(() => {
      this.reloadTimer = null
      if (!this.playerStarted) return
      if (this.hls) {
        this.hls.loadSource(src)
        this.hls.startLoad()
      } else if (this.hasVideoTarget) {
        this.videoTarget.load()
      }
    }, 3000)
  }

  clearReload() {
    if (this.reloadTimer) {
      clearTimeout(this.reloadTimer)
      this.reloadTimer = null
    }
  }

  showConnecting() {
    if (this.hasConnectingTarget) this.connectingTarget.classList.remove("hidden")
  }

  hideConnecting() {
    if (this.hasConnectingTarget) this.connectingTarget.classList.add("hidden")
  }

  // Try to play with sound; if the browser's autoplay policy blocks it, fall
  // back to muted autoplay (which is always permitted).
  attemptPlay(video) {
    const p = video.play()
    if (p && p.catch) {
      p.catch(() => {
        video.muted = true
        video.play().catch(() => {})
      })
    }
  }

  startHeartbeat() {
    if (this.heartbeatTimer) return
    this.heartbeatTimer = setInterval(() => {
      if (this.channel) {
        this.channel.perform("heartbeat")
      }
    }, 30000)
  }

  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }
}
